import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  CargoProblemDetail,
  CargoProblemWorkspaceCapabilities,
  CargoProblemWorkspaceNextAction,
} from '@magimp/shared';
import { Button } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { useTransitionCargoProblemWorkspace } from '../queries.js';
import { CargoProblemReassignModal } from './CargoProblemReassignModal.js';
import { CargoProblemTransitionModal } from './CargoProblemTransitionModal.js';

type NoteAction = Extract<CargoProblemWorkspaceNextAction, 'reject' | 'submitReview' | 'evaluate'>;

export function CargoProblemActions({
  problem,
  capabilities,
  onEdit,
}: {
  problem: CargoProblemDetail;
  capabilities: CargoProblemWorkspaceCapabilities;
  onEdit: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const showToast = useToastStore((state) => state.show);
  const transition = useTransitionCargoProblemWorkspace();
  const [noteAction, setNoteAction] = useState<NoteAction | null>(null);
  const [reassigning, setReassigning] = useState(false);

  async function run(action: 'submit' | 'claim') {
    try {
      await transition.mutateAsync({ id: problem.id, input: { action, attachments: [] } });
      showToast({ tone: 'success', text: t('toasts.transitioned') });
    } catch (error) {
      showToast({ tone: 'error', text: errorMessage(error) });
    }
  }

  const primary = primaryAction(
    capabilities.nextAction,
    problem.assignee === null,
    problem.status === 'REJECTED',
    transition.isPending,
    onEdit,
    run,
    setNoteAction,
    t,
  );
  const primaryIsEdit =
    capabilities.nextAction === 'submit' &&
    (problem.status === 'REJECTED' || problem.assignee === null);

  return (
    <>
      <div className="cargo-problem-actions">
        {capabilities.canEdit && !primaryIsEdit ? (
          <Button icon="pencil-line" onClick={onEdit}>
            {t('actions.edit')}
          </Button>
        ) : null}
        {capabilities.canReassign ? (
          <Button icon="user-cog" onClick={() => setReassigning(true)}>
            {t('actions.reassign')}
          </Button>
        ) : null}
        {capabilities.canReject && capabilities.nextAction !== 'reject' ? (
          <Button
            kind="danger"
            disabled={transition.isPending}
            onClick={() => setNoteAction('reject')}
          >
            {t('actions.reject')}
          </Button>
        ) : null}
        {primary}
      </div>
      {noteAction ? (
        <CargoProblemTransitionModal
          problemId={problem.id}
          action={noteAction}
          onClose={() => setNoteAction(null)}
        />
      ) : null}
      {reassigning ? (
        <CargoProblemReassignModal problem={problem} onClose={() => setReassigning(false)} />
      ) : null}
    </>
  );
}

function primaryAction(
  action: CargoProblemWorkspaceNextAction,
  unassigned: boolean,
  rejected: boolean,
  busy: boolean,
  onEdit: () => void,
  run: (action: 'submit' | 'claim') => Promise<void>,
  openNote: (action: NoteAction) => void,
  t: TFunction<'cargoProblems'>,
) {
  if (action === 'none') return null;
  if (action === 'submit') {
    return (
      <Button
        kind="primary"
        disabled={busy}
        onClick={rejected || unassigned ? onEdit : () => void run('submit')}
      >
        {rejected ? t('actions.editAndResubmit') : t('actions.submit')}
      </Button>
    );
  }
  if (action === 'claim') {
    return (
      <Button kind="primary" disabled={busy} onClick={() => void run('claim')}>
        {t('actions.claim')}
      </Button>
    );
  }
  const label =
    action === 'reject'
      ? t('actions.reject')
      : action === 'submitReview'
        ? t('actions.submitReview')
        : t('actions.evaluate');
  return (
    <Button
      kind={action === 'reject' ? 'danger' : 'primary'}
      disabled={busy}
      onClick={() => openNote(action)}
    >
      {label}
    </Button>
  );
}
